import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap } from 'rxjs/operators';
import { AccessTokenService } from '../app/services/access-token.service';
import { throwError } from 'rxjs';
import { AuthNavigationService } from '../app/services/auth-navigation.service';

const AUTH_SKIP_LIST = [
  '/api/v1/auth/token/client-credentials',
  '/api/v1/auth/refresh',
  'api/v1/auth/revoke',
  '/i18n/pt-BR.json',
  '/i18n/en-US.json',
  'https://www.primefaces.org/cdn/api/upload.php',
  'https://api.zippopotam.us/us',
];

const AUTH_ENDPOINTS_NO_REFRESH = [
  '/api/v1/auth/login',
  '/api/v1/customers/users/password/forgot',
  '/api/v1/users/password/reset',
  '/api/v1/user-actions/validate',
];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const accessTokenService = inject(AccessTokenService);
  const authNavigationService = inject(AuthNavigationService);

  const isAuthEndpoint = AUTH_ENDPOINTS_NO_REFRESH.some((x) =>
    req.url.includes(x)
  );

  const shouldSkip = AUTH_SKIP_LIST.some((x) => req.url.includes(x));
  if (shouldSkip) {
    const bare = AUTH_SKIP_LIST.some(
      () =>
        req.url.includes('upload.php') ||
        req.url.includes('https://api.zippopotam.us')
    )
      ? req.clone({ withCredentials: false })
      : req.clone({ withCredentials: true });
    return next(bare);
  }

  const alreadyRetried = req.headers.has('x-auth-retry');

  return accessTokenService.getValidToken$().pipe(
    switchMap((token) => {
      const withAuth = token
        ? req.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          })
        : req.clone({ withCredentials: true });

      return next(withAuth).pipe(
        catchError((err) => {
          if (
            err instanceof HttpErrorResponse &&
            err.status === 401 &&
            !isAuthEndpoint &&
            !alreadyRetried &&
            (accessTokenService.getCurrentMode() === 'private' ||
              accessTokenService.getSavedModeHint() === 'private')
          ) {
            return accessTokenService.forceRefreshPrivate$().pipe(
              switchMap((newToken) => {
                const retryReq = req.clone({
                  setHeaders: {
                    'x-auth-retry': '1',
                    Authorization: `Bearer ${newToken}`,
                  },
                  withCredentials: true,
                });
                return next(retryReq);
              }),
              catchError(() => next(req.clone({ withCredentials: true })))
            );
          }
          return throwError(() => err);
        })
      );
    }),
    catchError((err) => {
      if (isAuthEndpoint) return throwError(() => err);

      if (
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        accessTokenService.getCurrentMode() === 'private'
      ) {
        accessTokenService.clearSession();
        authNavigationService.router.navigateByUrl(
          authNavigationService.getUrlToRedirect('/auth/sign-in')
        );
      }

      return throwError(() => err);
    })
  );
};
